import React, { useState, useEffect } from 'react';
import { getAlldata } from '../controllers/APIController';

const TaskDataFetcher = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        
        const response = await getAlldata("tasks");
        
        // Handle different response structures
        let tasksData = [];
        if (response && Array.isArray(response)) {
          // Direct array response
          tasksData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          // Nested data response
          tasksData = response.data;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          // Double nested data response
          tasksData = response.data.data;
        } else {
          console.error("Unknown response structure:", response);
        }
        
        // Validate that we have an array of tasks
        if (!Array.isArray(tasksData)) {
          console.error("tasksData is not an array:", tasksData);
          tasksData = [];
        }
        
        if (tasksData.length > 0) {
          setTasks(tasksData);
        } else {
          setTasks([]);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  if (loading) {
    return (
      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
        <div className="text-emerald-700 font-medium">Loading real task data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <div className="text-red-700 font-medium">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
        <h3 className="text-lg font-semibold text-emerald-800 mb-2">
          Real Task Data from Collection
        </h3>
        <div className="text-sm text-emerald-700">
          Total tasks found: <strong>{tasks.length}</strong>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="text-yellow-700 font-medium">No tasks found in collection</div>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks
            .filter(task => task !== null) // Filter out null values from invalid tasks
            .map((task, index) => {
            // Ensure task is a valid object before processing
            if (!task || typeof task !== 'object') {
              console.warn('Invalid task object:', task);
              return null;
            }
            
                         const taskData = task.attributes || task;
             const metricData = taskData.metric?.data?.attributes || taskData.metric;
             const assignedUserData = taskData.assignedTo?.data?.attributes || taskData.assignedTo;
            
            return (
              <div key={task.id || index} className="p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 mb-2">
                      Task: {typeof taskData.Name === 'object' ? 'Unnamed Task' : (taskData.Name || 'Unnamed Task')}
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500">Task ID:</span>
                        <span className="ml-2 font-medium text-slate-700">
                          {typeof task.id === 'object' ? 'N/A' : (task.id || 'N/A')}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-slate-500">Status:</span>
                        <span className="ml-2 font-medium text-slate-700">
                          {typeof taskData.Status === 'object' ? 'N/A' : (taskData.Status || 'N/A')}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-slate-500">Priority:</span>
                        <span className="ml-2 font-medium text-slate-700">
                          {typeof taskData.Priority === 'object' ? 'N/A' : (taskData.Priority || 'N/A')}
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-slate-500">Progress:</span>
                        <span className="ml-2 font-medium text-slate-700">
                          {typeof taskData.Progress === 'object' ? 'N/A' : (taskData.Progress || 0)}%
                        </span>
                      </div>
                      
                      <div>
                        <span className="text-slate-500">Assigned To:</span>
                        <span className="ml-2 font-medium text-slate-700">
                          {assignedUserData ? (
                            typeof assignedUserData.username === 'string' ? assignedUserData.username :
                            typeof assignedUserData.email === 'string' ? assignedUserData.email :
                            typeof assignedUserData === 'string' ? assignedUserData : 'N/A'
                          ) : 'Not Assigned'}
                        </span>
                      </div>
                    </div>
                    
                    {taskData.Description && typeof taskData.Description !== 'object' && (
                      <div className="mt-3">
                        <span className="text-slate-500 text-sm">Description:</span>
                        <p className="text-sm text-slate-700 mt-1">{taskData.Description}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <h5 className="font-medium text-slate-800 mb-2">Linked Metric</h5>
                    {metricData ? (
                      <div className="text-sm">
                        <div>
                          <span className="text-slate-500">Name:</span>
                          <span className="ml-2 font-medium text-slate-700">
                            {typeof metricData.Name === 'object' || typeof metricData.name === 'object' 
                              ? 'Unnamed Metric' 
                              : (metricData.Name || metricData.name || 'Unnamed Metric')}
                          </span>
                        </div>
                        <div className="mt-1">
                          <span className="text-slate-500">ID:</span>
                          <span className="ml-2 font-medium text-slate-700">
                            {typeof taskData.metric?.data?.id === 'object' || typeof taskData.metric === 'object'
                              ? 'N/A' 
                              : (taskData.metric?.data?.id || taskData.metric || 'N/A')}
                          </span>
                        </div>
                        {metricData.Description && typeof metricData.Description !== 'object' && (
                          <div className="mt-1">
                            <span className="text-slate-500">Description:</span>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                              {metricData.Description}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500">
                        No metric linked
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h5 className="font-medium text-emerald-800 mb-2">Assigned User</h5>
                    {assignedUserData ? (
                      <div className="text-sm">
                        <div>
                          <span className="text-emerald-500">Username:</span>
                          <span className="ml-2 font-medium text-emerald-700">
                            {typeof assignedUserData.username === 'string' ? assignedUserData.username : 'N/A'}
                          </span>
                        </div>
                        <div className="mt-1">
                          <span className="text-emerald-500">Email:</span>
                          <span className="ml-2 font-medium text-emerald-700">
                            {typeof assignedUserData.username === 'string' ? assignedUserData.email : 'N/A'}
                          </span>
                        </div>
                        <div className="mt-1">
                          <span className="text-emerald-500">User ID:</span>
                          <span className="ml-2 font-medium text-emerald-700">
                            {typeof taskData.assignedTo?.data?.id === 'object' ? 'N/A' : (taskData.assignedTo?.data?.id || 'N/A')}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-emerald-500">
                        No user assigned
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Additional task details */}
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-600">
                    <div>
                      <span className="font-medium">Created:</span> {
                        taskData.CreationDate && typeof taskData.CreationDate !== 'object' 
                          ? new Date(taskData.CreationDate).toLocaleDateString() 
                          : 'N/A'
                      }
                    </div>
                    <div>
                      <span className="font-medium">Start Date:</span> {
                        taskData.StartDate && typeof taskData.StartDate !== 'object' 
                          ? new Date(taskData.StartDate).toLocaleDateString() 
                          : 'N/A'
                      }
                    </div>
                    <div>
                      <span className="font-medium">Due Date:</span> {
                        taskData.EstimatedEndDate && typeof taskData.EstimatedEndDate !== 'object' 
                          ? new Date(taskData.EstimatedEndDate).toLocaleDateString() 
                          : 'N/A'
                      }
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TaskDataFetcher;
